// ============================================================
// BancLecture — le banc avant/après d'une règle de LECTURE (étape 4).
// ------------------------------------------------------------
// Une règle qui vise un agent de lecture ne se valide qu'après ce banc :
// le vrai lecteur, relu sur des documents dont la vérité est relevée à
// l'œil, sans puis avec la règle. La carte montre son coût AVANT le clic,
// le suit pendant qu'il tourne (plusieurs minutes), puis dit son verdict
// agent par agent — score sans, score avec, et pourquoi.
//
// Ce que le banc ne dit pas est écrit aussi : une égalité veut dire
// « ne dégrade rien », pas « aide ».
// ============================================================

import { useEffect, useRef } from 'react';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import api from '../../lib/api.js';
import { formatDateTime, formatEuro } from '../../lib/format.js';

const VERDICTS = {
  passe: { ton: 'ok', libelle: 'Passé' },
  baisse: { ton: 'danger', libelle: 'Le score baisse' },
  barriere: { ton: 'danger', libelle: 'Barrière non franchie' },
  inconcluant: { ton: 'warn', libelle: 'Inconcluant' },
};

/** Le banc d'une règle de lecture est-il passé, sur le texte proposé ? */
export const bancPasse = (p) => p?.banc?.statut === 'termine' && p.banc.ok === true;

export function BancLecture({ proposition: p, peutLancer, envoi, onGeste, onRelire }) {
  const sim = p.simulation ?? {};
  const banc = p.banc ?? null;
  const enCours = banc?.statut === 'en_cours';

  // Pendant qu'il tourne, la carte se relit toutes les 15 secondes. Un échec
  // de relecture n'arrête rien : la suivante réessaie.
  const relire = useRef(onRelire);
  useEffect(() => { relire.current = onRelire; }, [onRelire]);
  useEffect(() => {
    if (!enCours) return undefined;
    const t = setInterval(() => {
      api.get(`/assistant/propositions/${p.id}`)
        .then((r) => relire.current?.(r.proposition))
        .catch(() => { /* réseau passager : la prochaine relecture réessaie */ });
    }, 15000);
    return () => clearInterval(t);
  }, [enCours, p.id]);

  const estime = (sim.bancs ?? []).reduce((t, b) => t + Number(b.cout_estime_eur ?? 0), 0);

  return (
    <div className="assistant-banc">
      <p><strong>Règle de lecture : elle ne s’active qu’après son banc.</strong></p>
      <p className="field__hint">
        Le vrai lecteur relit des documents dont la vérité est relevée à l’œil, sans puis avec la règle.
        Si le score baisse, elle ne peut pas être validée. Une égalité veut dire qu’elle ne dégrade rien — pas qu’elle aide.
      </p>
      {(sim.bancs ?? []).map((b) => (
        <p key={b.agent} className="field__hint">
          {`${b.libelle} : ${b.appels} appels, environ ${formatEuro(b.cout_estime_eur)}`}
          {b.absents?.length ? ` — ${b.absents.length} document(s) absent(s) du disque, non comptés : ${b.absents.join(', ')}` : ''}
        </p>
      ))}

      {enCours ? (
        <p className="field__hint" role="status">
          {`Banc en cours depuis ${formatDateTime(banc.debut)} (environ ${formatEuro(banc.cout_estime_eur)}). La carte se met à jour seule.`}
        </p>
      ) : null}
      {banc?.statut === 'erreur' ? (
        <p className="field__error" role="alert">{`Le banc n’a pas abouti : ${banc.erreur}`}</p>
      ) : null}
      {banc?.statut === 'termine' ? (
        <div>
          {Object.entries(banc.agents ?? {}).map(([agent, r]) => {
            const v = VERDICTS[r.statut] ?? VERDICTS.inconcluant;
            return (
              <div key={agent}>
                <p>
                  <Badge tone={v.ton}>{v.libelle}</Badge>
                  {` ${agent} — sans la règle ${r.avant.score}/${r.avant.sur}, avec ${r.apres.score}/${r.apres.sur} · ${formatEuro(r.cout_eur)}`}
                </p>
                <p className="field__hint">{r.raison}</p>
                <details className="repli">
                  <summary>Le détail, document par document</summary>
                  <ul>
                    {(r.details ?? []).map((d) => (
                      <li key={d.cas}>{`${d.cas} — sans : ${d.sans.join(' · ')} ; avec : ${d.avec.join(' · ')}`}</li>
                    ))}
                  </ul>
                </details>
              </div>
            );
          })}
          <p className="field__hint">{`Mesuré le ${formatDateTime(banc.fin)} · coût réel ${formatEuro(banc.cout_eur)}`}</p>
        </div>
      ) : null}

      {p.statut === 'pending' && peutLancer && !enCours ? (
        <Button size="sm" variant="secondary" disabled={envoi}
          onClick={() => onGeste(`/assistant/propositions/${p.id}/banc`)}>
          {`${banc ? 'Relancer' : 'Lancer'} le banc (environ ${formatEuro(estime)})`}
        </Button>
      ) : null}
    </div>
  );
}

export default BancLecture;
